import { Popover, PopoverDisclosure, usePopoverStore } from '@ariakit/react';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Search from '@ben/orion/search';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { CustomComponentTypes } from '@ben/shared/constants';
import type { Component } from '@ben/shared/types/columbia';
import type { FC, KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, useTheme } from 'styled-components';

interface IAddModularComponentsButtonProps {
  handleAddComponent: ({ type }: Component) => void;
  isSubForm?: boolean;
  isDisabled?: boolean;
}

const componentOptions = [
  { type: CustomComponentTypes.TEXT_INPUT, label: 'Text Input' },
  { type: CustomComponentTypes.AMOUNT_INPUT, label: 'Amount Field' },
  { type: CustomComponentTypes.CHECKBOX, label: 'Checkbox' },
  { type: CustomComponentTypes.TEXT_BLOCK, label: 'Text Block' },
  { type: CustomComponentTypes.DATE_INPUT, label: 'Date Input' },
  { type: CustomComponentTypes.PENSION_SLIDERS, label: 'Pension Sliders' },
  { type: CustomComponentTypes.BUY_SELL_HOLIDAY, label: 'Buy & Sell Holiday' },
  { type: CustomComponentTypes.DROPDOWN, label: 'Dropdown' },
  { type: CustomComponentTypes.FILE_UPLOAD, label: 'File Upload' },
  { type: CustomComponentTypes.MULTI_SELECT, label: 'Multiselect Field' },
  {
    type: CustomComponentTypes.MULTI_SELECT_WITH_SUBFORM,
    label: 'Multiselect Field w/ Subform',
  },
  { type: CustomComponentTypes.BENEFICIARIES, label: 'Beneficiaries' },
  { type: CustomComponentTypes.CONTACTS, label: 'Contacts' },
  { type: CustomComponentTypes.SLIDER, label: 'Slider' },
  { type: CustomComponentTypes.CALCULATED_VALUE, label: 'Calculated Value' },
];

const AddModularComponentsButton: FC<IAddModularComponentsButtonProps> = ({
  handleAddComponent,
  isSubForm,
  isDisabled,
}) => {
  const popover = usePopoverStore();
  const portalRoot = document.querySelector('#portal-root');
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const isExpanded = popover.useState().open;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, type: CustomComponentTypes) => {
      const eventKey = event.key;
      if (eventKey === 'Enter' || eventKey === ' ') {
        event.preventDefault();
        event.stopPropagation();
        handleAddComponent({
          type,
        } as Component);
      }
    },
    [handleAddComponent]
  );

  const filteredOptions = componentOptions.filter(({ label }) =>
    label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <PopoverDisclosure
        store={popover}
        render={
          <Button
            variant={ButtonVariant.primary}
            label={isSubForm ? 'Add subcomponent' : 'Add component'}
            width="fit-content"
            tabIndex={0}
            aria-expanded={isExpanded ? 'true' : 'false'}
            disabled={isDisabled}
          />
        }
      />
      {portalRoot &&
        createPortal(
          <StyledPopover store={popover} role="list">
            <SearchContainer>
              <Search
                placeholder="Search components"
                onChange={(event) => setSearchTerm(event.target.value)}
                width="100%"
              />
            </SearchContainer>
            <ListItem role="listitem">
              {filteredOptions.map(({ type, label }) => (
                <StyledTypography
                  variant={TypographyVariant.bodyMd2}
                  color={theme.palette.neutral[100]}
                  role="button"
                  onClick={() => handleAddComponent({ type } as Component)}
                  tabIndex={0} // Enables keyboard focus
                  onKeyDown={(event) => handleKeyDown(event, type)}
                  key={type}
                >
                  {label}
                </StyledTypography>
              ))}
            </ListItem>
          </StyledPopover>,
          portalRoot
        )}
    </>
  );
};

export default AddModularComponentsButton;

const StyledPopover = styled(Popover)`
  ${({ theme }) => css`
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid ${theme.palette.neutral[60]};
    background-color: ${theme.palette.neutral['00']};
    padding: ${theme.spacing(2, 0)};
    display: flex;
    flex-direction: column;
    min-width: 263px;
    z-index: 1000;
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
    cursor: pointer;
    padding: ${theme.spacing(1, 2)};
    border-bottom: 1px solid ${theme.palette.neutral[50]};

    &:hover,
    &:focus {
      background: ${theme.palette.neutral[60]};
    }
  `}
`;

const ListItem = styled.div`
  ${({ theme }) => css`
  max-height: 200px;
  overflow-y: auto;

  /* Hide scrollbar in Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    width: 5px; /* Width of the scrollbar */
    height: 5px; /* Height of the scrollbar */
  }

  /* Style the scrollbar thumb */
  ::-webkit-scrollbar-thumb {
    background-color: ${theme.palette.neutral[60]}; /* Color of the scrollbar thumb */
    border-radius: 5px; /* Radius of the scrollbar thumb */
    border: none; /* Border of the scrollbar thumb */
  }

  /* Hide the content in the padding section of the scrollbar */
  ::-webkit-scrollbar-track {
    background-color: transparent /* Background color of the padding section */
    border-radius: 5px; /* Radius of the padding section */
    border: none; /* Border of the padding section */
  }
  `}
`;

const SearchContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(0, 2)};
  `}
`;
